import { Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import firebase from 'firebase';
import { AuthService } from 'src/app/services/auth.service';

import { FilesComparisonComponent } from '../components/ui/files-comparison/files-comparison.component';
import { SimpleListSelectionComponent } from '../components/ui/simple-list-selection/simple-list-selection.component';
import { SimpleMessageWindowComponent } from '../components/ui/simple-message-window/simple-message-window.component';
import { CustomActions } from '../dictionaries/CustomActions';
import { ClientProfileComponent } from './../components/clientprofile/clientprofile.component';
import { ClientMatterService } from './client-matter.service';
import { UIMessagingService } from './uimessaging.service';
import { UtilsService } from './utils.service';
import { WindowsManagerService } from './windows-manager.service';
import { DialogService } from '../dialog.service';
import { environment } from 'src/environments/environment';
import { FileTransferV2Component } from '../components/file-transfer-v2/file-transfer-v2.component';
import { UserRoles } from '../dictionaries/UserRoles';
import { HttpClient } from '@angular/common/http';

const dbPatients = firebase.firestore().collection('patients');
const dbFiles = firebase.firestore().collection('files');
const CLIO_FOLDER_NAME = 'Clio';

export interface DocumentToImport {
  id: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class MattersService {
  authorizedUsers = environment.config.betaTesters;
  isAuthorized: boolean;
  constructor(
    public dialog_$: DialogService,
    public dialog: MatDialog,
    public auth_$: AuthService,
    private ng: NgZone,
    private http: HttpClient,
    private utils_$: UtilsService,
    private clientMatter_$: ClientMatterService,
    private router: Router,
    private uiMessaging_$: UIMessagingService,
    private windowsManager_$: WindowsManagerService,
  ) {
    this.isAuthorized = this.authorizedUsers.includes(this.auth_$.userData.getValue()['email']);
  }

  getDocument(clioFileId: string) {
    return this.http
      .post('https://us-central1-nuagedx-dev.cloudfunctions.net/clio-getDocument', {
        clioDocId: clioFileId,
        userdocid: this.auth_$.userData.getValue()['id'],
        eu: this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0,
      })
      .toPromise();
  }

  async importFilesFromClio(selectedFiles: any[], caseName: string, transferType: string, clioObjectId: string) {
    // Clean parentPath from selectedFiles
    selectedFiles.forEach(file => {
      file.parentPath = '';
      file.parentFolder = '';
    });

    // Call to a cloud function onCall from here.
    return firebase
      .functions()
      .httpsCallable('clio-handleClioFilesImportV2')({
        userdocid: this.auth_$.userData.getValue()['id'],
        filesList: selectedFiles,
        caseName: caseName,
        transferType: transferType,
        clioObjectId: clioObjectId,
      })
      .then(result => {
        console.log('result :', result);
        return result;
      });
  }

  async checkClientMatterExistsFromClioMatterId(matterId: any) {
    const ownerid = this.auth_$.userData.getValue()['ownerID'] || this.auth_$.userData.getValue()['uid'];
    this.auth_$.showLoader('Checking if matter exists...', 'checking-matter-exists');
    const result = await dbPatients
      .where('clioDocId', '==', parseInt(matterId, 10))
      .where('forDelete', '==', false)
      .where('owners', 'array-contains', ownerid)
      .limit(1)
      .get();
    this.auth_$.hideLoader();
    return result;
  }

  async getMatterInfoFromDocumentId(documentId: any) {
    this.auth_$.showLoader('Getting matter info from Clio...', 'getting-matter-info');
    const eu = this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0;
    const result = await firebase.functions().httpsCallable('clio-getMatterInfoFromDocumentIdV2')({
      documentId,
      userdocid: this.auth_$.userData.getValue()['id'],
      eu,
    });
    this.auth_$.hideLoader();
    return result;
  }

  async getMatterIdFromFolderId(folderId: any) {
    this.auth_$.showLoader('Getting matter info from Clio...', 'getting-matter-info');
    const result = await firebase.functions().httpsCallable('clio-getMatterInfoFromFolderId')({
      folderId,
      userdocid: this.auth_$.userData.getValue()['id'],
    });
    this.auth_$.hideLoader();
    return result;
  }

  async getClioDocumentInfo(documentId: any) {
    this.auth_$.showLoader('Getting document info from Clio...', 'getting-document-info');
    const result = await firebase.functions().httpsCallable('clio-getClioDocumentInfo')({
      documentId,
      userdocid: this.auth_$.userData.getValue()['id'],
    });
    this.auth_$.hideLoader();
    return result;
  }

  async importClioMatter(matterId, user_id, options) {
    this.importMatterFromClio(matterId, user_id, options);
  }

  async getDocumentsListFromClioMatterId(matterId: string, userdocid: string, eu: number) {
    this.auth_$.showLoader('Getting documents list from Clio...', 'getting-documents-list');
    const result = await firebase.functions().httpsCallable('clio-getDocumentsFromMatterIdV2')({
      matterId,
      userdocid,
      eu,
    });
    this.auth_$.hideLoader();
    return result;
  }

  async getDocumentsAndFoldersListFromClioMatterId(matterId: string, userdocid: string, eu: number) {
    this.auth_$.showLoader('Getting documents list from Clio...', 'getting-documents-list');
    const result = await firebase.functions().httpsCallable('clio-getDocumentsAndFoldersFromMatterId')({
      matterId,
      userdocid,
      eu,
    });
    this.auth_$.hideLoader();
    return result;
  }

  async getFilesAndFolderFromFolderId(folderId: string) {
    this.auth_$.showLoader('Getting documents list from Clio...', 'getting-documents-list');
    const eu = this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0;
    return firebase
      .functions()
      .httpsCallable('clio-getDocumentsAndFoldersFromFolderId')({
        folderId,
        userdocid: this.auth_$.userData.getValue()['id'],
        eu,
      })
      .then(result => {
        this.auth_$.hideLoader('getting-documents-list');
        return result;
      });
  }

  // async getDocumentsAndFoldersListFromClioFolderId(folderId: string) {
  //   const eu = this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0;
  //   const userdocid = this.auth_$.userData.getValue()['id'];
  //   const params = { folderId, userdocid, eu };
  //   const result = await firebase.functions().httpsCallable('clio-getDocumentsAndFoldersFromFolderId')(params);
  //   return result;
  // }

  async getDocumentsListFromCaseName(caseName: any, parentFolder) {
    this.auth_$.showLoader('Getting documents list from Clio...', 'getting-documents-list');
    const result = (await dbFiles.where('belongsTo', '==', caseName).where('origin', '==', 'clio').get()).docs.map(
      doc => doc.data(),
    );
    this.auth_$.hideLoader();
    return result;
  }

  async getClioFolderIdFromCaseName(caseName) {
    this.auth_$.showLoader('Getting Clio folder id...', 'getting-clio-folder-id');
    const folderId = (
      await dbFiles
        .where('belongsTo', '==', caseName)
        .where('type', '==', 'folder')
        .where('name', '==', CLIO_FOLDER_NAME)
        .limit(1)
        .get()
    ).docs[0]?.data().folderId;
    this.auth_$.hideLoader();
    return folderId;
  }

  async getFolderContentsV2(folderId: string) {
    // Validate the folderId
    if (!folderId) {
      console.error('Invalid folderId:', folderId);
      return null;
    }

    this.auth_$.showLoader('Getting folder content from Clio...', 'getting-folder-content');

    const result = await firebase.functions().httpsCallable('clio-getDocumentsFromFolderIdV4')({
      folderId: folderId,
      userdocid: this.auth_$.userData.getValue()['id'],
      eu: this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0,
    });
    this.auth_$.hideLoader();
    console.log('getFolderContent result :', result);
    return result;
  }

  async getFileContentsV2(clioFileId: string) {
    // Validate the clioFileId
    if (!clioFileId) {
      console.error('Invalid clioFileId:', clioFileId);
      return null;
    }

    this.auth_$.showLoader('Getting folder content from Clio...', 'getting-folder-content');

    const result = await firebase.functions().httpsCallable('clio-getDocumentsFromFolderIdV4')({
      clioFileId: clioFileId,
      userdocid: this.auth_$.userData.getValue()['id'],
      eu: this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0,
    });
    this.auth_$.hideLoader();
    console.log('getFolderContent result :', result);
    return result;
  }

  async getMatterContent(matterId) {
    this.auth_$.showLoader('Getting matter content from Clio...', 'getting-matter-content');
    const result = await firebase.functions().httpsCallable('clio-getDocumentsFromMatterIdV3')({
      matterId,
      userdocid: this.auth_$.userData.getValue()['id'],
      eu: this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0,
    });
    this.auth_$.hideLoader();
    console.log('getMatterContent result :', result);
    return result;
  }

  async getNuagedxMatterContent(caseName) {
    return firebase.functions().httpsCallable('files-getNuageDxDocumentsFromMatterIdV3')({ caseName });
  }

  async getMatterContentV2(matterId) {
    this.auth_$.showLoader('Getting matter content from Clio...', 'getting-matter-content');
    const result = await firebase.functions().httpsCallable('clio-getDocumentsFromMatterIdV4')({
      matterId: matterId,
      userdocid: this.auth_$.userData.getValue()['id'],
      eu: this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0,
    });
    this.auth_$.hideLoader();
    console.log('getMatterContent result :', result);
    return result;
  }

  async getMatterContentV3(matterId) {
    this.auth_$.showLoader('Getting matter content from Clio...', 'getting-matter-content');
    const result = await firebase.functions().httpsCallable('clio-getDocumentsFromMatterIdV4_1')({
      matterId: matterId,
      userdocid: this.auth_$.userData.getValue()['id'],
      eu: this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0,
    });
    this.auth_$.hideLoader();
    console.log('getMatterContent result :', result);
    return result;
  }

  openFilesComparisonModal(
    sourceDocuments: {
      id: string;
      name: string;
      type: string;
      size: number;
    }[],
    localDocuments: {
      id: string;
      name: string;
      type: string;
      size: number;
    }[],
    matterId: string,
    options: {
      clioDocId: string;
      matterData: any;
      type: string;
      targetFolder?: string;
      comparison?: boolean;
    },
  ) {
    this.ng.run(async () => {
      if (this.isAuthorized) {
        this.dialog_$.open(FileTransferV2Component, {
          id: 'compare-open-files-dialog',
          width: '95vw',
          top: '5%',
          data: {
            clioFolderId: options.matterData.clioDocId,
            caseName: options.matterData.caseName,
            clioDocId: options.clioDocId,
            type: options.type,
          },
        });
      } else {
        this.dialog
          .open(FilesComparisonComponent, {
            minWidth: '900px',
            data: {
              sourceDocuments: sourceDocuments,
              localDocuments: localDocuments,
              matterData: options.matterData,
              options: options,
              userEmail: this.auth_$.userData.getValue()['email'],
              matterContent: (await this.getMatterContent(matterId)).data.data.data,
            },
          })
          .afterClosed()
          .subscribe(async result => this.handleComparisonModalResult(result, matterId, options));
      }
    });
    return this.dialog;
  }

  async handleComparisonModalResult(result, matterId, options) {
    if (result) {
      let importOptions;
      if (result.new) {
        importOptions = { option: 'selected', destiny: options.clioFolderId, selectedDocuments: result.selected };
        await this.importDocumentsFromClioV2(matterId, options.caseName, importOptions);
      } else {
        importOptions = { option: 'selected', destiny: options.clioFolderId, selectedDocuments: result };
        await this.importDocumentsFromClio(matterId, options.caseName, importOptions);
      }
    } else {
      console.log('No documents were selected');
      this.uiMessaging_$.toastMessage(
        'No documents were selected. Please select the documents you want to import.',
        'warning',
      );
    }

    this.openExistingMatter(options.matterData, this.auth_$.userData.getValue()['id'], {
      ...options,
      targetFolder: CLIO_FOLDER_NAME,
      comparison: false,
    });
  }

  /**
   * Opens a client matter from Clio in the application
   * @param matterId The Clio matter ID
   * @param userId The current user ID
   * @param options Additional options for opening the matter
   * @returns A promise that resolves when the matter is opened
   */
  async openClioClientMatter(matterId: string | number, userId: string, options: any = {}): Promise<void> {
    console.log('Opening Clio client matter:', { matterId, userId, options });

    // Set document ID from object ID if coming from document open action
    if (options.target === CustomActions.openClioDocument) {
      options.clioDocumentId = options.clioObjectId;
    }

    // Find existing client matter or import a new one
    const foundClientMatter = await this.searchClioMatter(matterId);
    let clientMatterData;
    let params: { [key: string]: any } = { targetFolder: CLIO_FOLDER_NAME };

    if (foundClientMatter.length > 0) {
      clientMatterData = foundClientMatter[0].data();

      // Handle different opening scenarios
      if (options.target === CustomActions.openClioDocument) {
        params.clioDocumentId = options.clioDocumentId || options.clioObjectId;
      } else if (options.section) {
        params.section = options.section;
      } else if (!options.section && options.target) {
        params.section = options.target;
      }
    } else {
      try {
        clientMatterData = await this.importMatterOnly(matterId, userId);
        if (!clientMatterData) {
          this.uiMessaging_$.toastMessage('Failed to import matter from Clio', 'ERROR');
          return;
        }
      } catch (error) {
        console.error('Error importing matter:', error);
        this.uiMessaging_$.toastMessage('Error importing matter from Clio', 'ERROR');
        return;
      }
    }

    // Open the matter
    this.openExistingMatter(clientMatterData, userId, params);
  }

  getClioMatterIdFromDocumentId(documentId) {
    return firebase.functions().httpsCallable('clio-getMatterInfoByClioDocumentId')({
      documentId,
      userdocid: this.auth_$.userData.getValue()['id'],
    });
  }

  openExistingMatter(clientMatterData: any, user_id: any, options?: any) {
    const { target, section, clioDocumentId, targetFolder } = options || {};
    if (!this.windowsManager_$.clientProfileOpened) {
      this.ng.run(() => {
        const dialog = this.dialog_$.open(ClientProfileComponent, {
          data: {
            patient: clientMatterData,
            user_id: user_id,
            section: target || section || null,
            clioDocumentId: clioDocumentId || null,
            clioMatterId: clientMatterData.clioDocId,
            targetFolder: targetFolder || null,
          },
          width: '100vw',
          id: 'client-profile',
        });
        dialog.afterClosed().subscribe(() => {
          this.windowsManager_$.clientProfileOpened = false;
          this.redirectToHome();
          if (this.auth_$.isClioRegistered()) {
            this.auth_$.drawer.open();
          }
        });
        dialog.afterOpened().subscribe(() => {
          this.windowsManager_$.clientProfileOpened = true;
        });
      });
    }
  }

  private redirectToHome() {
    this.router.navigate(['/']);
  }

  async importMatterFromClio(matterId: any, user_id: any, options?: any) {
    let message = `The matter you are trying to open doesn't exist on NuagedX yet. Do you want to import it now?`;
    let modalOptions = [
      { label: 'Select what documents to import', value: 'selected', selected: true },
      { label: 'Import all documents', value: 'all', selected: false },
      { label: 'Do not import any document.', value: 'none', selected: false },
    ];

    if (options.target) {
      switch (options.target) {
        case CustomActions.openClioDocument:
          message =
            `The document "${options.documentMatterInfo.filename}" you are trying to open belongs to a matter ` +
            `that doesn't exist on NuagedX yet. Do you want to import it now?`;
          modalOptions = [
            { label: `Import just "${options.documentMatterInfo.filename}"`, value: 'document', selected: true },
            { label: 'Select what documents to import', value: 'selected', selected: false },
            { label: 'Import all documents', value: 'all', selected: false },
            { label: 'Do not import any document.', value: 'none', selected: false },
          ];
          break;
        case CustomActions.openClioMatter:
          message = `The matter you are trying to open doesn't exist on NuagedX yet. Do you want to import it now?`;
          break;
        case CustomActions.shareDisc:
          message = `The disc in the matter you are trying to open doesn't exist on NuagedX yet. Do you want to import it now?`;
          break;
        case CustomActions.uploadDicomDisk:
          message =
            `You are trying to upload a DICOM disk inside a matter that doesn't exist on NuagedX yet. ` +
            `Do you want to import it now?`;
          break;
        case CustomActions.viewMedicalImages:
          message =
            `You are trying to view Medical Images inside a matter that doesn't exist on NuagedX yet. ` +
            `Do you want to import it now?`;
          break;
        default:
          message = `The matter you are trying to open doesn't exist on NuagedX yet. Do you want to import it now?`;
          break;
      }
    }

    this.ng.run(() => {
      this.dialog
        .open(SimpleMessageWindowComponent, {
          data: {
            title: 'Import Matter from Clio',
            message: message,
            buttons: [
              { text: 'Yes', value: true },
              { text: 'No', value: false },
            ],
            options: modalOptions,
            complexAnswer: true,
          },
          width: '500px',
          closeOnNavigation: false,
          disableClose: true,
        })
        .afterClosed()
        .subscribe(async ({ answer, importOption }) => {
          if (answer) this.handleAnswer(importOption, options, matterId, user_id);
          else {
            this.uiMessaging_$.toastMessage('The import process has been cancelled.', 'INFORMATION');
            this.redirectToHome();
          }
        });
    });
  }

  async importMatterOnly(matterId, user_id) {
    this.auth_$.showLoader('Importing matter from Clio...', 'importing-matter');
    const clioMatterInfo = await this.getClioMatterInfo(matterId);
    console.log('clioMatterInfo :', clioMatterInfo);
    let data;

    if (clioMatterInfo) {
      data = clioMatterInfo['data']['data'];
    } else {
      this.auth_$.hideLoader();
      return;
    }

    const cleanMattterDisplayNumber = this.utils_$.cleanString(data.display_number);

    // NOTE: IMPORTANT: NEEDS TO BE TESTED
    let ownerID: string;
    if (this.auth_$.userData.getValue()['role'] === UserRoles.owner) {
      ownerID = this.auth_$.userData.getValue()['uid'];
    } else if (this.auth_$.userData.getValue()['owners']?.length > 1) {
      // Prompt the user to select an owner
      this.dialog
        .open(SimpleListSelectionComponent, {
          data: {
            title: 'Select the owner',
            items: this.auth_$.userData.getValue()['owners'],
            buttons: [
              { text: 'Continue', value: true },
              { text: 'Cancel', value: false },
            ],
          },
        })
        .afterClosed()
        .toPromise()
        .then(async ({ data }) => {
          if (data) {
            ownerID = data;
          } else {
            this.redirectToHome();
          }
        });
    } else if (this.auth_$.userData.getValue()['owners'].length === 1) {
      // User the only owner available
      ownerID = this.auth_$.userData.getValue()['owners'][0];
    } else {
      // No owners available, handle the error or show a message
      this.uiMessaging_$.toastMessage('No owners available.', 'ERROR');
      this.redirectToHome();
      return;
    }

    // Create the client/matter in NuagedX.
    const newMatterData = await this.createClioMatter(data, ownerID, cleanMattterDisplayNumber);

    // Create default folders.
    await this.clientMatter_$.createDefaultFolders(cleanMattterDisplayNumber);

    // Create Clio folder.
    const clioFolderId = await this.clientMatter_$.createCustomFolder(cleanMattterDisplayNumber, CLIO_FOLDER_NAME);

    this.auth_$.hideLoader();
    return { ...newMatterData, clioFolderId };
  }

  async createClioMatter(data, ownerID, cleanMattterDisplayNumber) {
    this.auth_$.showLoader('Creating the Client/Matter in NuagedX...', 'creating-client-matter');
    const { client, id, number, description } = data;
    const { name, type, initials, first_name, last_name } = client;
    const newClientMatter = await this.clientMatter_$.createClientMatter({
      localData: {},
      ownerID: ownerID,
      matterData: { client, id, number, display_number: cleanMattterDisplayNumber, description },
      clientData: { name, type, initials, first_name, last_name },
    });
    this.auth_$.hideLoader();
    return { ...newClientMatter, clioData: data };
  }

  async handleAnswer(importOption, options, matterId, user_id) {
    this.auth_$.showLoader('Getting Matter info from Clio...', 'getting-matter-info');
    const clioMatterInfo = await this.getClioMatterInfo(matterId);
    this.auth_$.hideLoader();
    let data;

    if (clioMatterInfo) data = clioMatterInfo['data']['data'];

    const { client, id, number, display_number, description } = data;
    const { name, type, initials, first_name, last_name } = client;

    this.auth_$.showLoader('Creating the Client/Matter in NuagedX...', 'creating-client-matter');
    const cleanMattterDisplayNumber = this.utils_$.cleanString(display_number);

    // NOTE: IMPORTANT: NEEDS TO BE TESTED
    let ownerID: string;
    if (this.auth_$.userData.getValue()['role'] === UserRoles.owner) {
      ownerID = this.auth_$.userData.getValue()['uid'];
    } else if (this.auth_$.userData.getValue()['owners'].length > 1) {
      // Prompt the user to select an owner
      this.dialog
        .open(SimpleListSelectionComponent, {
          data: {
            title: 'Select the owner',
            items: this.auth_$.userData.getValue()['owners'],
            buttons: [
              { text: 'Continue', value: true },
              { text: 'Cancel', value: false },
            ],
          },
        })
        .afterClosed()
        .toPromise()
        .then(async ({ data }) => {
          if (data) {
            ownerID = data;
          } else {
            this.redirectToHome();
          }
        });
    } else if (this.auth_$.userData.getValue()['owners'].length === 1) {
      // User the only owner available
      ownerID = this.auth_$.userData.getValue()['owners'][0];
    } else {
      // No owners available, handle the error or show a message
      this.uiMessaging_$.toastMessage('No owners available.', 'ERROR');
      this.redirectToHome();
      return;
    }

    // Create the client/matter in NuagedX.
    await this.clientMatter_$.createClientMatter({
      localData: {},
      ownerID: ownerID,
      matterData: { client, id, number, display_number: cleanMattterDisplayNumber, description },
      clientData: { name, type, initials, first_name, last_name },
    });

    this.auth_$.hideLoader();

    this.auth_$.showLoader('Creating your default folders...', 'creating-default-folders');

    // Create default folders.
    await this.clientMatter_$.createDefaultFolders(cleanMattterDisplayNumber);
    this.auth_$.hideLoader();

    let clioFolderId;

    if (importOption && importOption !== 'none') {
      this.auth_$.showLoader('Creating your Clio folder...', 'creating-clio-folder');
      clioFolderId = await this.clientMatter_$.createCustomFolder(cleanMattterDisplayNumber, CLIO_FOLDER_NAME);
      this.auth_$.hideLoader();
    }

    this.handleImportOption(importOption, matterId, cleanMattterDisplayNumber, clioFolderId, user_id, options);
  }

  async handleAllDocuments(importedDocuments, matterId, cleanMattterDisplayNumber, clioFolderId, options?) {
    this.auth_$.showLoader('Importing all documents from Clio...', 'importing-all-documents');
    importedDocuments = await this.importDocumentsFromClio(matterId, cleanMattterDisplayNumber, {
      option: 'all',
      destiny: clioFolderId,
    });
    console.log('importedDocuments :', importedDocuments);
    if (importedDocuments && importedDocuments.data.length > 0) {
      this.uiMessaging_$.toastMessage('The matter and its documents were imported successfully.', 'INFORMATION');
    }
    this.auth_$.showLoader('Opening the matter...', 'opening-matter');
    this.openClioClientMatter(matterId, this.auth_$.userData.getValue()['uid'], options).then(() =>
      this.auth_$.hideLoader(),
    );
  }

  async handleSelected(matterId, cleanMattterDisplayNumber, clioFolderId, user_id, importedDocuments, options?) {
    this.auth_$.showLoader('Getting your selected documents from Clio...', 'getting-selected-documents');
    const clioDocuments = await this.getClioDocumentsList(matterId);
    this.auth_$.hideLoader();
    if (!clioDocuments) {
      this.dialog
        .open(SimpleMessageWindowComponent, {
          data: {
            title: 'No documents found',
            message: 'No documents found in Clio for this matter. Do you want to import the matter without documents?',
            buttons: [
              { text: 'Yes', value: true },
              { text: 'No', value: false },
            ],
          },
          disableClose: true,
        })
        .afterClosed()
        .toPromise()
        .then(answer => {
          if (!answer) {
            this.redirectToHome();
          } else {
            this.auth_$.showLoader('Importing matter from Clio...', 'importing-matter');
            this.importMatterFromClio(matterId, user_id).then(() => {
              this.auth_$.hideLoader();
            });
          }
        });
      return;
    }

    await this.dialog
      .open(SimpleListSelectionComponent, {
        data: {
          title: 'Select the documents to import',
          items: clioDocuments['data'],
          buttons: [
            { text: 'Continue', value: true },
            { text: 'Cancel', value: false },
          ],
        },
      })
      .afterClosed()
      .toPromise()
      .then(async ({ data }) => {
        if (data.length > 0) {
          this.auth_$.showLoader('Importing selected documents from Clio...', 'importing-selected-documents');
          importedDocuments = await this.importDocumentsFromClio(matterId, cleanMattterDisplayNumber, {
            option: 'selected',
            selectedDocuments: data,
            destiny: clioFolderId,
          });
          this.auth_$.hideLoader();

          if (importedDocuments) {
            if (importedDocuments.data.length > 0) {
              this.uiMessaging_$.toastMessage(
                'The matter and its documents were imported successfully.',
                'INFORMATION',
              );
              this.auth_$.showLoader('Opening the matter...');
              this.openClioClientMatter(matterId, this.auth_$.userData.getValue()['uid'], options).then(() =>
                this.auth_$.hideLoader(),
              );
            }
          } else {
            this.uiMessaging_$.toastMessage("The matter wan't imported successfully.", 'INFO');
          }
        } else {
          console.log('No data');
        }
      });
  }

  async handleImportOption(importOption, matterId, cleanMattterDisplayNumber, clioFolderId, user_id, options) {
    let importedDocuments;
    switch (importOption) {
      case 'all':
        this.handleAllDocuments(importedDocuments, matterId, cleanMattterDisplayNumber, clioFolderId, options);
        break;
      case 'selected':
        this.handleSelected(matterId, cleanMattterDisplayNumber, clioFolderId, user_id, importedDocuments, options);
        break;
      case 'none':
        this.handleNone(matterId, user_id, options);
        break;
      case 'document':
        this.handleDocument(matterId, cleanMattterDisplayNumber, clioFolderId, user_id, importedDocuments, options);
        break;
      default:
        break;
    }
  }

  async handleDocument(matterId, cleanMattterDisplayNumber, clioFolderId, user_id, importedDocuments, options) {
    const data = [{ id: options.documentMatterInfo.id, name: options.documentMatterInfo.filename }];
    this.auth_$.showLoader('Importing selected documents from Clio...', 'importing-selected-documents');
    importedDocuments = await this.importDocumentsFromClio(matterId, cleanMattterDisplayNumber, {
      option: 'selected',
      selectedDocuments: data,
      destiny: clioFolderId,
    });
    this.auth_$.hideLoader();

    if (importedDocuments) {
      if (importedDocuments.data.length > 0) {
        this.uiMessaging_$.toastMessage('The matter and its documents were imported successfully.', 'INFO');
        this.auth_$.showLoader('Opening the matter...', 'opening-matter');
        this.openClioClientMatter(matterId, this.auth_$.userData.getValue()['uid'], options).then(() =>
          this.auth_$.hideLoader(),
        );
      }
    } else {
      this.uiMessaging_$.toastMessage("The matter wan't imported successfully.", 'INFO');
    }
  }

  handleNone(matterId, user_id, options?) {
    this.uiMessaging_$.toastMessage('The matter was imported successfully.', 'Close');

    this.dialog
      .open(SimpleMessageWindowComponent, {
        data: {
          title: 'Import Matter from Clio',
          message: 'The matter has been imported successfully.',
          buttons: [
            {
              text: 'View your imported client/matter',
              value: true,
            },
          ],
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.auth_$.showLoader('Opening the matter...', 'opening-matter');
          this.openExistingMatter(matterId, user_id);
          this.auth_$.hideLoader();
        } else {
          this.dialog.closeAll();
        }
      });
  }

  async getClioDocumentsList(matterId) {
    const documents = (await this.getDocumentsByMatterId(matterId))['data'];
    if (!documents || documents['code'] !== 200) {
      console.error('error :', documents);
      return false;
    }
    return documents['data'];
  }

  getDocumentsByMatterId(matterId) {
    const eu = this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0;
    return firebase.functions().httpsCallable('clio-getDocumentsFromMatterIdV3')({
      matterId: matterId,
      userdocid: this.auth_$.userData.getValue()['id'],
      eu,
    });
  }

  async runImportDocumentsFromClio(documentsToImport, matterId, destiny?): Promise<any> {
    const eu = this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0;
    const importResult: { data: any } = await firebase.functions().httpsCallable('clio-importDocumentsV3')({
      documents: documentsToImport,
      matter: matterId,
      userdocid: this.auth_$.userData.getValue()['id'],
      destiny: destiny,
      eu,
    });

    if (importResult.data === false) {
      this.uiMessaging_$.toastMessage('The import process has been cancelled.', 'INFORMATION');
      this.redirectToHome();
    } else {
      return importResult.data;
    }
  }

  async runImportDocumentsFromClioV4(documentsToImport, matterId, destiny?): Promise<any> {
    const eu = this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0;
    const importResult: { data: any } = await firebase.functions().httpsCallable('clio-importDocumentsV4')({
      documents: documentsToImport,
      matter: matterId,
      userdocid: this.auth_$.userData.getValue()['id'],
      destiny: destiny,
      eu,
    });

    if (importResult.data === false) {
      this.uiMessaging_$.toastMessage('The import process has been cancelled.', 'INFORMATION');
      this.redirectToHome();
    } else {
      return importResult.data;
    }
  }

  async getDocumentsToImport(matterId): Promise<DocumentToImport[]> {
    return (
      await firebase.functions().httpsCallable('clio-getDocumentsToImport')({
        matterId: matterId,
        userdocid: this.auth_$.userData.getValue()['id'],
      })
    ).data;
  }

  async importDocumentsFromClio(
    matterId: any,
    newClientMattter: string,
    importOptions?: { option: string; destiny: string; selectedDocuments?: any[] },
  ): Promise<any> {
    const { option, selectedDocuments } = importOptions;
    let documentsToImport: DocumentToImport[];

    switch (option) {
      case 'all':
        documentsToImport = await this.getDocumentsToImport(matterId);
        break;
      case 'selected':
        documentsToImport = selectedDocuments;
        break;
      default:
        break;
    }

    if (!documentsToImport || documentsToImport.length === 0) {
      this.uiMessaging_$.toastMessage('No documents to import', 'INFORMATION');
      return false;
    }

    return this.runImportDocumentsFromClio(documentsToImport, newClientMattter, importOptions.destiny);
  }

  async importDocumentsFromClioV2(
    matterId: any,
    newClientMattter: string,
    importOptions?: { option: string; destiny: string; selectedDocuments?: any[] },
  ): Promise<any> {
    const { option, selectedDocuments } = importOptions;
    let documentsToImport: DocumentToImport[];

    switch (option) {
      case 'all':
        documentsToImport = await this.getDocumentsToImport(matterId);
        break;
      case 'selected':
        documentsToImport = selectedDocuments;
        break;
      default:
        break;
    }

    if (!documentsToImport || documentsToImport.length === 0) {
      this.uiMessaging_$.toastMessage('No documents to import', 'INFORMATION');
      return false;
    }

    return this.runImportDocumentsFromClioV4(documentsToImport, newClientMattter, importOptions.destiny);
  }

  getRandomString(length) {
    const randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
    }
    return result;
  }

  /**
   * Searches for a Clio matter in the NuagedX database
   * @param clioDocId The Clio document ID to search for
   * @returns A promise that resolves to an array of matching documents
   */
  async searchClioMatter(clioDocId: string | number): Promise<firebase.firestore.QueryDocumentSnapshot[]> {
    try {
      this.auth_$.showLoader('Searching for this matter in NuagedX...', 'searching-matter');

      // Parse the ID as integer and ensure it's a valid number
      const parsedId = parseInt(String(clioDocId), 10);
      if (isNaN(parsedId)) {
        console.error('Invalid Clio document ID:', clioDocId);
        return [];
      }

      // Get the current user's owner ID
      const ownerID = this.auth_$.userData.getValue()?.['ownerID'] || this.auth_$.userData.getValue()?.['uid'];

      // Query for the matter
      const answer = await dbPatients
        .where('clioDocId', '==', parsedId)
        .where('forDelete', '==', false)
        .where('owners', 'array-contains', ownerID)
        .limit(1)
        .get();

      return answer.docs;
    } catch (error) {
      console.error('Error searching for Clio matter:', error);
      return [];
    } finally {
      this.auth_$.hideLoader();
    }
  }

  async getClioMatterInfo(matterId) {
    this.auth_$.showLoader('Getting matter info from Clio...', 'getting-matter-info');
    const userdocid = this.auth_$.userData.getValue()['id'];
    const clientField = `client{id,name,first_name,last_name,type,title,primary_email_address,primary_phone_number,initials,middle_name,clio_connect_email}`;
    const fields = `${clientField},id,number,display_number,description`;
    const eu = this.auth_$.userData.getValue()['clioRegion'] === 'eu' ? 1 : 0;
    const params = { matterId, userdocid, fields, eu };
    const cloudFunctionName = 'clio-getMatterInfoV3';
    const response = await firebase
      .functions()
      .httpsCallable(cloudFunctionName)(params)
      .catch(err => {
        console.log(err);
        return false;
      });

    if (!response) {
      this.uiMessaging_$.toastMessage('Error getting matter info from Clio', 'ERROR');
      return false;
    }
    this.auth_$.hideLoader();
    return response;
  }
}
